import { OrbitControls } from '@react-three/drei';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Suspense, useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import * as THREE from 'three';
import { useViewportWidth } from '../hooks/useViewportWidth';
import evaluationReport from '../../artifacts/humanoid-evaluation.json';
import {
  advanceHumanoidLabPhysics,
  fireBalanceNudge,
  fireHitImpulse,
  fireMuscleBalancePush,
  fireShoveImpulse,
  fireSmashImpulse,
  getExistingHumanoidLabRuntime,
  getHumanoidLabRuntime,
  MUSCLE_PUSH_CERTIFIED_LATERAL_NS,
  MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS,
  MUSCLE_PUSH_STRESS_LATERAL_NS,
  MUSCLE_PUSH_STRESS_SAGITTAL_NS,
  respawnHumanoidLabScenario,
  setHumanoidLabHeight,
  setHumanoidLabLimitPreset,
  setHumanoidLabMass,
  setHumanoidLabPoseHoldStrength,
  setHumanoidLabScenario,
  setHumanoidMuscleFatigueMode,
  setHumanoidMuscleWeakLeftLeg,
  setMusclePushStressMode,
  requestHumanoidOneStep,
  requestHumanoidSingleSupportHold,
  setHumanoidP1ATrialStyleKnobs,
  type HumanoidLabRuntime,
  type HumanoidMuscleTelemetry,
  type MusclePushLifecycle,
  type HumanoidScenario,
  type MuscleBalancePushDirection,
  type MusclePushTier,
} from '../physics/humanoidLabPhysics';
import {
  IMITATION_DEMO_STYLE_PRESET_V2,
  IMITATION_DEMO_STYLE_PRESET_V2_NOTE,
} from '../physics/humanoidImitationSearch';
import type { P1ATrialStyleKnobs } from '../physics/humanoidMuscleController';
import {
  createHumanoidVisual,
  createMuscleBalanceMarkers,
  createStaticBoxesVisual,
  type HumanoidVisual,
  type MuscleBalanceMarkers,
  type StaticSceneryVisual,
} from '../physics/humanoidVisual';
import type { HumanoidBoneName, JointLimitPreset } from '../physics/humanoidBones';
import type {
  MusclePhase,
  MusclePushDirection,
  MuscleStepPhase,
  MuscleSwingSide,
} from '../physics/humanoidMuscleController';
import './HumanoidLab.css';

interface HumanoidLabGates {
  settle: boolean;
  restEnergy: boolean;
  limits: boolean;
  integrity: boolean;
  determinism: boolean;
  pile: boolean;
  perf: boolean;
  poseHold: boolean;
  getUp: boolean;
}

// window.__humanoidLabState is the E2E / future-contract bridge. The plan
// (plans/humanoid-lab-plan.md §5) fixes { ready, scenario, bodyCount,
// awakeCount, settled, stepMs, pelvisHeight, poseHoldActive, gates, suitePass
// } — all present below, unchanged. `pelvisDisplacementM` is ADDITIVE: the
// fixed shape has no position field, but the plan's own §6 "drag" assertion
// ("pelvis displaced >0.5m laterally") is unobservable without one. Adding a
// field is compatible with "keep this shape stable" (nothing listed moved or
// was removed).
/** Additive muscleBalance telemetry on the E2E bridge (stable shape). */
interface HumanoidLabMuscleState {
  controllerActive: boolean;
  controlMode: 'muscle-inspired';
  phase: MusclePhase;
  peakActivation: number;
  meanActivation: number;
  fatigue: number;
  weakLeftLeg: boolean;
  centerOfMass: readonly [number, number, number];
  supportCenter: readonly [number, number, number];
  supportErrorM: number;
  pelvisTiltDeg: number;
  lastPushDirection: MusclePushDirection;
  /** ADDITIVE cp22: certified MVP vs stress limit. */
  lastPushTier: MusclePushTier | null;
  /** ADDITIVE cp22: last push magnitude (N·s). */
  lastPushImpulseNs: number;
  pushLifecycle: MusclePushLifecycle;
  pushElapsedS: number;
  pushPeakSupportErrorM: number;
  pushPeakTiltDeg: number;
  pushPeakDisplacementM: number;
  /** ADDITIVE cp22: UI stress mode (primary buttons fire 12/11). */
  pushStressMode: boolean;
  forbiddenAssistCount: number;
  /** ADDITIVE P1A: capture-step / single-support sub-phase (`none` when idle). */
  stepPhase: MuscleStepPhase;
  /** ADDITIVE P1A: which foot is swinging during a step/hold; null when idle. */
  swingSide: MuscleSwingSide | null;
  /** ADDITIVE P1A presentation: live swing-foot lift height (m) from controller. */
  swingLiftM: number;
}

interface HumanoidLabState {
  ready: boolean;
  scenario: HumanoidScenario;
  bodyCount: number;
  awakeCount: number;
  settled: boolean;
  stepMs: number;
  pelvisHeight: number;
  poseHoldActive: boolean;
  gates: HumanoidLabGates;
  suitePass: boolean;
  pelvisDisplacementM: number;
  /** ADDITIVE: whether the boosted get-up controller is currently hoisting
   *  the rig back to a stand (Alive/post-Hit auto-recovery). */
  gettingUp: boolean;
  /** ADDITIVE: muscleBalance controller telemetry; absent/inactive elsewhere. */
  muscle?: HumanoidLabMuscleState;
}

/** Dev/E2E actions for imitation demo capture (eval-only style knobs + step). */
interface HumanoidLabActions {
  /** Apply imitation v2 demo preset (NOT CERTIFIED). */
  applyImitationDemoPresetV2: () => boolean;
  /** Apply arbitrary eval-only style knobs (null clears). */
  setStyleKnobs: (knobs: Readonly<P1ATrialStyleKnobs> | null) => boolean;
  /** Queue explicit forward one-step (default 0.10 m). */
  requestForwardStep: (forwardM?: number) => boolean;
  /** Queue certified P1A single-support hold (fallback capture). */
  requestHold: (swingSide?: 'left' | 'right') => boolean;
  /** Preset note string (NOT CERTIFIED). */
  imitationDemoNote: string;
}

declare global {
  interface Window {
    __humanoidLabState?: HumanoidLabState;
    __humanoidLabActions?: HumanoidLabActions;
  }
}

const GATES: HumanoidLabGates = {
  settle: evaluationReport.settle.pass,
  restEnergy: evaluationReport.restEnergy.pass,
  limits: evaluationReport.limits.pass,
  integrity: evaluationReport.integrity.pass,
  determinism: evaluationReport.determinism.pass,
  pile: evaluationReport.pile.pass,
  perf: evaluationReport.perf.pass,
  poseHold: evaluationReport.poseHold.pass,
  getUp: evaluationReport.getUp.pass,
};
const SUITE_PASS = evaluationReport.engineeringPass;

function initialHumanoidScenario(): HumanoidScenario {
  const path = window.location.pathname.toLowerCase();
  const params = new URLSearchParams(window.location.search);
  const scenarioParam = params.get('scenario') ?? params.get('mode');
  if (
    path.includes('/humanoid-lab/muscle-balance') ||
    scenarioParam === 'muscleBalance' ||
    scenarioParam === 'muscle-balance'
  ) {
    return 'muscleBalance';
  }
  return 'balance';
}

function isMuscleBalanceDemoRoute(): boolean {
  return window.location.pathname.toLowerCase().includes('/humanoid-lab/muscle-balance');
}

// 'balance' first among certified modes; muscleBalance is the separate
// no-cheat experimental demo. Existing scenario ids/testids stay stable.
const SCENARIOS: ReadonlyArray<{ id: HumanoidScenario; label: string; blurb: string }> = [
  { id: 'balance', label: 'Alive', blurb: 'stands, take a swing at him' },
  { id: 'muscleBalance', label: 'Muscle Balance', blurb: 'muscle-inspired · experimental' },
  { id: 'drop', label: 'Crash: Drop', blurb: '2m fall · expected collapse' },
  { id: 'hit', label: 'Crash: Hit', blurb: 'upper-body impulse · expected knockdown' },
  { id: 'stairs', label: 'Crash: Stairs', blurb: '5-step tumble · expected fall' },
  { id: 'drag', label: 'Crash: Drag', blurb: 'kinematic wrist pull' },
  { id: 'pile', label: 'Crash: Pile', blurb: '10-rig crowd drop' },
];

const EMPTY_MUSCLE: HumanoidLabMuscleState = {
  controllerActive: false,
  controlMode: 'muscle-inspired',
  phase: 'idle',
  peakActivation: 0,
  meanActivation: 0,
  fatigue: 0,
  weakLeftLeg: false,
  centerOfMass: [0, 0, 0],
  supportCenter: [0, 0, 0],
  supportErrorM: 0,
  pelvisTiltDeg: 0,
  lastPushDirection: null,
  lastPushTier: null,
  lastPushImpulseNs: 0,
  pushLifecycle: 'idle',
  pushElapsedS: 0,
  pushPeakSupportErrorM: 0,
  pushPeakTiltDeg: 0,
  pushPeakDisplacementM: 0,
  pushStressMode: false,
  forbiddenAssistCount: 0,
  stepPhase: 'none',
  swingSide: null,
  swingLiftM: 0,
};

/** Demo slow-mo for airborne one-leg hold phases (presentation pacing only). */
const DEMO_SLOW_MO_SCALE = 0.3;
const DEMO_SLOW_MO_RAMP_S = 0.3;

function isAirborneStepPhase(phase: MuscleStepPhase): boolean {
  return phase === 'lift' || phase === 'hold' || phase === 'place' || phase === 'load';
}

function muscleStateFromTelemetry(tel: HumanoidMuscleTelemetry): HumanoidLabMuscleState {
  return {
    controllerActive: tel.controllerActive,
    controlMode: tel.controlMode,
    phase: tel.phase,
    peakActivation: tel.peakActivation,
    meanActivation: tel.meanActivation,
    fatigue: tel.fatigue,
    weakLeftLeg: tel.weakLeftLeg,
    centerOfMass: tel.centerOfMass,
    supportCenter: tel.supportCenter,
    supportErrorM: tel.supportErrorM,
    pelvisTiltDeg: tel.pelvisTiltDeg,
    lastPushDirection: tel.lastPushDirection,
    lastPushTier: tel.lastPushTier,
    lastPushImpulseNs: tel.lastPushImpulseNs,
    pushLifecycle: tel.pushLifecycle,
    pushElapsedS: tel.pushElapsedS,
    pushPeakSupportErrorM: tel.pushPeakSupportErrorM,
    pushPeakTiltDeg: tel.pushPeakTiltDeg,
    pushPeakDisplacementM: tel.pushPeakDisplacementM,
    pushStressMode: tel.pushStressMode,
    forbiddenAssistCount: tel.forbiddenAssistCount,
    stepPhase: tel.stepPhase,
    swingSide: tel.swingSide,
    swingLiftM: tel.swingLiftM,
  };
}

/** Human-readable P1A phase label for the lab status line. */
function singleSupportStatusLabel(
  stepPhase: MuscleStepPhase,
  swingSide: MuscleSwingSide | null,
  swingLiftM = 0,
  slowMoActive = false,
): string {
  if (stepPhase === 'none') return 'idle';
  const side = swingSide ? ` · ${swingSide}` : '';
  const airborne = isAirborneStepPhase(stepPhase);
  // Round lift to 0.1 cm for a stable HUD (controller meters → cm).
  const liftCm = Math.round(swingLiftM * 1000) / 10;
  const lift = airborne ? ` · lift ${liftCm.toFixed(1)} cm` : '';
  const slow = slowMoActive ? ' · slow-mo 0.3x' : '';
  return `${stepPhase}${side}${lift}${slow}`;
}

const GATE_LABELS: ReadonlyArray<[string, keyof HumanoidLabGates]> = [
  ['SETTLE', 'settle'],
  ['REST', 'restEnergy'],
  ['LIMITS', 'limits'],
  ['INTEGRITY', 'integrity'],
  ['DET', 'determinism'],
  ['PILE', 'pile'],
  ['PERF', 'perf'],
  ['POSE', 'poseHold'],
  ['GETUP', 'getUp'],
];

interface HudStats {
  bodyCount: number;
  awakeCount: number;
  stepMs: number;
  settled: boolean;
  pelvisHeight: number;
  poseHoldActive: boolean;
  gettingUp: boolean;
  muscle: HumanoidLabMuscleState;
}

const EMPTY_STATS: HudStats = {
  bodyCount: 0,
  awakeCount: 0,
  stepMs: 0,
  settled: false,
  pelvisHeight: 0,
  poseHoldActive: false,
  gettingUp: false,
  muscle: { ...EMPTY_MUSCLE },
};

export default function HumanoidLab({ onBack }: { onBack: () => void }) {
  const [scenario, setScenario] = useState<HumanoidScenario>(() => initialHumanoidScenario());
  const [limitPreset, setLimitPreset] = useState<JointLimitPreset>('game');
  const [heightM, setHeightM] = useState(1.8);
  const [massKg, setMassKg] = useState(75);
  const [poseHoldStrength, setPoseHoldStrength] = useState(1);
  const [weakLeftLeg, setWeakLeftLeg] = useState(false);
  const [fatigueMode, setFatigueMode] = useState(false);
  const [pushStressMode, setPushStressMode] = useState(false);
  const [resetToken, setResetToken] = useState(0);
  const [ready, setReady] = useState(false);
  const [stats, setStats] = useState<HudStats>(EMPTY_STATS);
  const [controlsOpen, setControlsOpen] = useState(false);
  const viewportWidth = useViewportWidth();
  const isNarrowCanvas = viewportWidth <= 640;
  const isMobile = viewportWidth <= 900;
  const isMuscle = scenario === 'muscleBalance';
  const isFocusedMuscleDemo = isMuscleBalanceDemoRoute();
  // The muscle-balance story is intentionally a focused surface even when it
  // is selected from the general lab route. Keep the rig/scenario/engineering
  // controls available for the other scenarios, but remove them from this
  // demo so the push → response story stays primary.
  const isMuscleDemoUi = isFocusedMuscleDemo || isMuscle;

  const statsRef = useRef<HudStats>(EMPTY_STATS);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const snap = statsRef.current;
      setStats({ ...snap, muscle: { ...snap.muscle } });
    }, 200);
    return () => window.clearInterval(interval);
  }, []);

  // Keep local toggle UI in sync after scenario reset (runtime clears weakness/fatigue).
  useEffect(() => {
    if (scenario !== 'muscleBalance' || resetToken > 0) {
      setWeakLeftLeg(false);
      setFatigueMode(false);
      setPushStressMode(false);
    }
  }, [scenario, resetToken]);

  const handleReady = useCallback(() => setReady(true), []);

  const fireMusclePush = useCallback((direction: MuscleBalancePushDirection) => {
    const rt = getExistingHumanoidLabRuntime();
    if (rt) fireMuscleBalancePush(rt, direction, { tier: pushStressMode ? 'stress' : 'certified' });
  }, [pushStressMode]);

  const fireSingleSupportHold = useCallback((swingSide: MuscleSwingSide) => {
    const rt = getExistingHumanoidLabRuntime();
    if (rt) requestHumanoidSingleSupportHold(rt, { swingSide });
  }, []);

  // Eval-only imitation demo plumbing for headed capture / window actions.
  useEffect(() => {
    if (!isMuscle) {
      delete window.__humanoidLabActions;
      return;
    }
    window.__humanoidLabActions = {
      imitationDemoNote: IMITATION_DEMO_STYLE_PRESET_V2_NOTE,
      applyImitationDemoPresetV2: () => {
        const rt = getExistingHumanoidLabRuntime();
        if (!rt) return false;
        setHumanoidP1ATrialStyleKnobs(rt, IMITATION_DEMO_STYLE_PRESET_V2);
        return true;
      },
      setStyleKnobs: (knobs) => {
        const rt = getExistingHumanoidLabRuntime();
        if (!rt) return false;
        setHumanoidP1ATrialStyleKnobs(rt, knobs);
        return true;
      },
      requestForwardStep: (forwardM = 0.1) => {
        const rt = getExistingHumanoidLabRuntime();
        if (!rt) return false;
        return requestHumanoidOneStep(rt, {
          forwardDisplacementM: forwardM,
          swingSide: 'right',
        });
      },
      requestHold: (swingSide = 'right') => {
        const rt = getExistingHumanoidLabRuntime();
        if (!rt) return false;
        return requestHumanoidSingleSupportHold(rt, { swingSide });
      },
    };
    return () => {
      delete window.__humanoidLabActions;
    };
  }, [isMuscle]);

  const singleSupportBusy = stats.muscle.stepPhase !== 'none';

  const visibleScenarios = isFocusedMuscleDemo
    ? SCENARIOS.filter((item) => item.id === 'muscleBalance')
    : SCENARIOS;

  const scenarioChips = (
    <div className="hl-scenarios">
      {visibleScenarios.map((item) => (
        <button
          key={item.id}
          type="button"
          data-testid={`humanoid-scenario-${item.id}`}
          className={scenario === item.id ? 'active' : ''}
          onClick={() => setScenario(item.id)}
        >
          <strong>{item.label}</strong>
          <span>{item.blurb}</span>
        </button>
      ))}
    </div>
  );

  const demoGuide = isMuscle ? (
    <div className="hl-demo-guide" data-testid="muscle-demo-guide">
      <strong>The demo</strong>
      <ol>
        <li>Use certified pushes first: side 2 N·s, front/back 4 N·s.</li>
        <li>Expected result: Manny should brace, wobble, and stay upright.</li>
        <li>Watch activation rise while forbidden assists stay at zero.</li>
        <li>Turn on Weak left leg or Fatigue mode to reduce torque capacity.</li>
        <li>Use Stress limit only to show the documented fall boundary.</li>
      </ol>
    </div>
  ) : null;

  const muscleOutcome = isMuscle ? (
    <div className="hl-muscle-outcome" data-testid="muscle-expected-outcome">
      {stats.muscle.lastPushDirection ? (
        <>
          <div className={`hl-impact-cue hl-impact-${stats.muscle.pushLifecycle}`} data-testid="muscle-impact-cue">
            <span className="hl-impact-arrow" aria-hidden="true">
              {stats.muscle.lastPushDirection === 'left' ? '←' : stats.muscle.lastPushDirection === 'right' ? '→' : stats.muscle.lastPushDirection === 'forward' ? '↑' : '↓'}
            </span>
            <span><strong>Impact {stats.muscle.lastPushDirection} · {stats.muscle.lastPushImpulseNs} N·s</strong><small>measured by Box3D state</small></span>
          </div>
          <strong data-testid="muscle-push-result">
            {stats.muscle.pushLifecycle === 'pending'
              ? 'MEASURING RESPONSE…'
              : stats.muscle.pushLifecycle === 'recovered'
                ? 'PASS · RECOVERED'
                : stats.muscle.lastPushTier === 'stress' && stats.muscle.pushLifecycle === 'fallen'
                  ? 'EXPECTED LIMIT · FALLEN'
                  : 'FAIL · FALLEN'}
          </strong>
          {stats.muscle.lastPushTier === 'stress' && stats.muscle.pushLifecycle === 'fallen' ? (
            <span>Expected boundary: stress limit demonstrates the documented fall, not a recovery claim.</span>
          ) : null}
          <span>Peak tilt {stats.muscle.pushPeakTiltDeg.toFixed(1)}° · support error {(stats.muscle.pushPeakSupportErrorM * 100).toFixed(1)} cm · displacement {(stats.muscle.pushPeakDisplacementM * 100).toFixed(1)} cm</span>
          <strong>
            Last push: {stats.muscle.lastPushDirection} · {stats.muscle.lastPushTier ?? '—'} ·{' '}
            {stats.muscle.lastPushImpulseNs || '—'} N·s
          </strong>
          {stats.muscle.lastPushTier === 'stress' ? (
            <span>Expected: the rig should fall. This is the documented limit, not a recovery claim.</span>
          ) : stats.muscle.phase === 'fallen' ? (
            <span>Expected: stay upright. Current state: fallen, so this would be a visible failure.</span>
          ) : (
            <span>Expected: brace, wobble, keep feet planted, and remain upright without hidden assists.</span>
          )}
        </>
      ) : (
        <>
          <strong>Tap a certified push.</strong>
          <span>Expected: a small wobble and visible activation, not a dramatic fall.</span>
        </>
      )}
    </div>
  ) : null;

  const rigSection = (
    <ControlSection title="Rig">
      <Slider testId="humanoid-height" label="Height" value={heightM} min={1.4} max={2.1} step={0.02} suffix=" m" onChange={setHeightM} />
      <Slider testId="humanoid-mass" label="Mass" value={massKg} min={40} max={120} step={1} suffix=" kg" onChange={setMassKg} />
      <div className="hl-segmented">
        {(['game', 'anatomical'] as const).map((preset) => (
          <button
            key={preset}
            type="button"
            data-testid={`humanoid-limit-${preset}`}
            className={limitPreset === preset ? 'active' : ''}
            onClick={() => setLimitPreset(preset)}
          >
            {preset === 'game' ? 'Game' : 'Anatomical'}
          </button>
        ))}
      </div>
    </ControlSection>
  );

  const activeControlSection = (
    <ControlSection title="Active control">
      <Slider
        testId="humanoid-strength"
        label={isMuscle ? 'Strength' : 'Pose-hold strength'}
        value={poseHoldStrength}
        min={0}
        max={1}
        step={0.05}
        suffix=""
        onChange={setPoseHoldStrength}
      />
      <div className="hl-actions-row">
        <button
          type="button"
          data-testid="humanoid-nudge"
          disabled={scenario !== 'balance'}
          onClick={() => {
            const rt = getExistingHumanoidLabRuntime();
            if (rt) fireBalanceNudge(rt);
          }}
        >
          Nudge
        </button>
        <button
          type="button"
          data-testid="humanoid-rehit"
          disabled={scenario !== 'hit'}
          onClick={() => {
            const rt = getExistingHumanoidLabRuntime();
            if (rt) fireHitImpulse(rt);
          }}
        >
          Re-hit
        </button>
      </div>
    </ControlSection>
  );

  const muscleControlSection = isMuscle ? (
    <ControlSection title="Muscle Balance">
      <div className="hl-muscle-banner" data-testid="muscle-demo-banner">
        <strong>Goal demo · muscle-inspired, no-cheat balance</strong>
        <p>
          Joint motors and contact only — not medical or anatomical accuracy. Primary pushes are the
          certified MVP envelope (side {MUSCLE_PUSH_CERTIFIED_LATERAL_NS} N·s, front/back{' '}
          {MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS} N·s). Stress mode uses the documented fall limit (
          {MUSCLE_PUSH_STRESS_LATERAL_NS}/{MUSCLE_PUSH_STRESS_SAGITTAL_NS} N·s) — expected to topple.
        </p>
      </div>
      <div className="hl-push-tier" data-testid="muscle-push-tier-label">
        {pushStressMode ? (
          <span className="hl-tier-stress">
            Stress limit · {MUSCLE_PUSH_STRESS_LATERAL_NS}/{MUSCLE_PUSH_STRESS_SAGITTAL_NS} N·s · expected fall
          </span>
        ) : (
          <span className="hl-tier-certified">
            Certified push · side {MUSCLE_PUSH_CERTIFIED_LATERAL_NS} · front/back{' '}
            {MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS} N·s
          </span>
        )}
      </div>
      {muscleOutcome}
      <div className="hl-push-grid" role="group" aria-label="Directional pushes">
        <span className="hl-push-spacer" />
        <button type="button" data-testid="muscle-push-forward" onClick={() => fireMusclePush('forward')}>
          Forward
        </button>
        <span className="hl-push-spacer" />
        <button type="button" data-testid="muscle-push-left" onClick={() => fireMusclePush('left')}>
          Left
        </button>
        <button type="button" data-testid="muscle-push-backward" onClick={() => fireMusclePush('backward')}>
          Back
        </button>
        <button type="button" data-testid="muscle-push-right" onClick={() => fireMusclePush('right')}>
          Right
        </button>
      </div>
      <div className="hl-actions-row">
        <button
          type="button"
          data-testid="muscle-stress-mode"
          className={pushStressMode ? 'active' : ''}
          aria-pressed={pushStressMode}
          title={`Stress limit ${MUSCLE_PUSH_STRESS_LATERAL_NS}/${MUSCLE_PUSH_STRESS_SAGITTAL_NS} N·s — expected fall`}
          onClick={() => {
            const next = !pushStressMode;
            setPushStressMode(next);
            const rt = getExistingHumanoidLabRuntime();
            if (rt) setMusclePushStressMode(rt, next);
          }}
        >
          Stress limit
        </button>
        <button
          type="button"
          data-testid="muscle-weak-left"
          className={weakLeftLeg ? 'active' : ''}
          aria-pressed={weakLeftLeg}
          onClick={() => {
            const next = !weakLeftLeg;
            setWeakLeftLeg(next);
            const rt = getExistingHumanoidLabRuntime();
            if (rt) setHumanoidMuscleWeakLeftLeg(rt, next);
          }}
        >
          Weak left leg
        </button>
        <button
          type="button"
          data-testid="muscle-fatigue"
          className={fatigueMode ? 'active' : ''}
          aria-pressed={fatigueMode}
          onClick={() => {
            const next = !fatigueMode;
            setFatigueMode(next);
            const rt = getExistingHumanoidLabRuntime();
            if (rt) setHumanoidMuscleFatigueMode(rt, next);
          }}
        >
          Fatigue mode
        </button>
      </div>
      <div className="hl-single-support" role="group" aria-label="One-leg hold">
        <div className="hl-single-support-label">One-leg hold</div>
        <div className="hl-actions-row">
          <button
            type="button"
            data-testid="muscle-single-support-hold-left"
            disabled={singleSupportBusy}
            title="Shift weight onto right leg, lift left foot, hold, place"
            onClick={() => fireSingleSupportHold('left')}
          >
            Lift left
          </button>
          <button
            type="button"
            data-testid="muscle-single-support-hold-right"
            disabled={singleSupportBusy}
            title="Shift weight onto left leg, lift right foot, hold, place"
            onClick={() => fireSingleSupportHold('right')}
          >
            Lift right
          </button>
          <button
            type="button"
            data-testid="muscle-imitation-forward-step"
            disabled={singleSupportBusy}
            title={IMITATION_DEMO_STYLE_PRESET_V2_NOTE}
            onClick={() => {
              const rt = getExistingHumanoidLabRuntime();
              if (!rt) return;
              setHumanoidP1ATrialStyleKnobs(rt, IMITATION_DEMO_STYLE_PRESET_V2);
              requestHumanoidOneStep(rt, {
                forwardDisplacementM: 0.1,
                swingSide: 'right',
              });
            }}
          >
            Style step (demo)
          </button>
        </div>
        <div
          className={`hl-single-support-status${isAirborneStepPhase(stats.muscle.stepPhase) ? ' slow-mo' : ''}`}
          data-testid="muscle-single-support-status"
        >
          {singleSupportStatusLabel(
            stats.muscle.stepPhase,
            stats.muscle.swingSide,
            stats.muscle.swingLiftM,
            isAirborneStepPhase(stats.muscle.stepPhase),
          )}
        </div>
      </div>
    </ControlSection>
  ) : null;

  const resetAction = (
    <div className="hl-actions">
      <button type="button" data-testid="humanoid-reset" onClick={() => setResetToken((value) => value + 1)}>
        Reset scenario
      </button>
    </div>
  );

  // Dev-only, E2E-only guaranteed-knockdown button (Alive mode) — see
  // fireSmashImpulse's comment in humanoidLabPhysics.ts. Visually hidden
  // (zero footprint, no layout/HUD impact) but still Playwright-actionable:
  // opacity/position hiding, not display:none, which Playwright's click
  // actionability checks would reject.
  const smashDevButton = (
    <button
      type="button"
      data-testid="humanoid-smash"
      aria-hidden="true"
      style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: scenario === 'balance' ? 'auto' : 'none' }}
      onClick={() => {
        const rt = getExistingHumanoidLabRuntime();
        if (rt) fireSmashImpulse(rt);
      }}
    />
  );

  const gateStrip = (
    <div className="hl-gates" data-testid="humanoid-gate-strip">
      {GATE_LABELS.map(([label, key]) => (
        <i key={key} className={GATES[key] ? 'pass' : 'fail'}>{label}</i>
      ))}
    </div>
  );

  return (
    <div className={`hl-page ${isFocusedMuscleDemo ? 'hl-focused-muscle-demo' : ''}`}>
      {smashDevButton}
      <header className="hl-header">
        <button type="button" className="hl-back" onClick={onBack}>Back</button>
        <div className="hl-title">
          <span>HUMANOID PHYSICS LAB / REPORT v{evaluationReport.reportVersion}</span>
          <h1>{isFocusedMuscleDemo ? 'Box3D muscle balance — certified push demo.' : 'Manny — the default procedural ragdoll.'}</h1>
        </div>
        <div
          className={`hl-suite ${isMuscle ? 'experimental' : SUITE_PASS ? 'pass' : 'fail'}`}
          data-testid="humanoid-certification"
        >
          {isMuscle ? 'EXPERIMENTAL · GATES OPEN' : `ENGINEERING ${SUITE_PASS ? 'PASS' : 'FAIL'}`}
        </div>
      </header>

      <main className="hl-main">
        {!isMobile && (
          <aside className="hl-controls">
            {!isFocusedMuscleDemo && (
              <div className="hl-quickbar">
                <ControlSection title="Scenario">{scenarioChips}</ControlSection>
              </div>
            )}

            <div className="hl-drawer">
              {demoGuide}
              {!isMuscleDemoUi && rigSection}
              {!isMuscleDemoUi && activeControlSection}
              {muscleControlSection}
              {resetAction}
            </div>
          </aside>
        )}

        <section className="hl-workspace">
          <div className="hl-stage">
            <Canvas
              shadows
              camera={isNarrowCanvas
                ? { position: [2.7, 2.15, 3.45], fov: 46, near: 0.05, far: 70 }
                : { position: [2.8, 2.25, 3.5], fov: 40, near: 0.05, far: 60 }}
              onCreated={({ gl }) => gl.setClearColor('#0a0d13')}
            >
              <Suspense fallback={null}>
                <ambientLight intensity={0.7} />
                <directionalLight position={[3, 6, 4]} intensity={2.2} castShadow />
                <hemisphereLight args={['#3a4a6a', '#05060a', 0.5]} />
                <Ground />
                <HumanoidPhysicsLayer
                  scenario={scenario}
                  limitPreset={limitPreset}
                  heightM={heightM}
                  massKg={massKg}
                  poseHoldStrength={poseHoldStrength}
                  resetToken={resetToken}
                  statsRef={statsRef}
                  onReady={handleReady}
                />
                <OrbitControls makeDefault target={[0, 1, 0]} minDistance={1.2} maxDistance={22} />
              </Suspense>
            </Canvas>
            {!ready && <div className="hl-loading">Assembling Manny…</div>}
            {isMobile && (
              <div className="hl-mobile-stats" data-testid="humanoid-hud">
                {isMuscle ? (
                  <>
                    <MobileStatChip label="Phase" value={stats.muscle.phase} />
                    <MobileStatChip label="Act" value={`${(stats.muscle.meanActivation * 100).toFixed(0)}%`} />
                    <MobileStatChip label="Peak" value={`${(stats.muscle.peakActivation * 100).toFixed(0)}%`} />
                    <MobileStatChip label="Fatigue" value={`${(stats.muscle.fatigue * 100).toFixed(0)}%`} />
                    <MobileStatChip label="Pelvis" value={`${stats.pelvisHeight.toFixed(2)}m`} />
                    <MobileStatChip label="Tilt" value={`${stats.muscle.pelvisTiltDeg.toFixed(0)}°`} />
                  </>
                ) : (
                  <>
                    <MobileStatChip label="Bodies" value={stats.bodyCount.toString()} />
                    <MobileStatChip label="Awake" value={stats.awakeCount.toString()} />
                    <MobileStatChip label="Step" value={`${stats.stepMs.toFixed(1)}ms`} />
                    <MobileStatChip label="Pelvis" value={`${stats.pelvisHeight.toFixed(2)}m`} />
                    <MobileStatChip label="Pose" value={stats.poseHoldActive ? 'ON' : 'OFF'} />
                  </>
                )}
              </div>
            )}
            {isMobile && isMuscle && (
              <div
                className={`hl-mobile-proof hl-mobile-proof-${stats.muscle.pushLifecycle}`}
                data-testid="muscle-mobile-proof"
              >
                <div className="hl-mobile-proof-result" data-testid="muscle-mobile-result">
                  <span className="hl-mobile-proof-arrow" aria-hidden="true">
                    {stats.muscle.lastPushDirection === 'left'
                      ? '←'
                      : stats.muscle.lastPushDirection === 'right'
                        ? '→'
                        : stats.muscle.lastPushDirection === 'forward'
                          ? '↑'
                          : stats.muscle.lastPushDirection === 'backward'
                            ? '↓'
                            : '→'}
                  </span>
                  <span>
                    <strong>
                      {stats.muscle.pushLifecycle === 'recovered'
                        ? 'PASS · RECOVERED'
                        : stats.muscle.pushLifecycle === 'fallen' && stats.muscle.lastPushTier === 'stress'
                          ? 'EXPECTED LIMIT · FALLEN'
                          : stats.muscle.pushLifecycle === 'fallen'
                            ? 'FAIL · FALLEN'
                            : stats.muscle.pushLifecycle === 'pending'
                              ? 'IMPACT · RECOVERING'
                              : 'PUSH → BALANCE → PASS'}
                    </strong>
                    <small>
                      {stats.muscle.lastPushDirection
                        ? `${stats.muscle.weakLeftLeg ? 'WEAK LEFT · ' : ''}${stats.muscle.fatigue >= 0.45 ? `FATIGUE ${(stats.muscle.fatigue * 100).toFixed(0)}% · ` : ''}${stats.muscle.lastPushDirection} ${stats.muscle.lastPushImpulseNs} N·s · ${stats.muscle.pushPeakTiltDeg.toFixed(1)}° tilt · ${(stats.muscle.pushPeakDisplacementM * 100).toFixed(1)} cm shift`
                        : 'Tap a certified push — real Box3D state'}
                    </small>
                  </span>
                </div>
                <div className="hl-mobile-proof-meters">
                  <label>
                    <span>Balance error</span>
                    <i><b style={{ width: `${Math.min(100, (stats.muscle.supportErrorM / 0.12) * 100)}%` }} /></i>
                  </label>
                  <label>
                    <span>Muscle drive</span>
                    <i><b style={{ width: `${Math.min(100, stats.muscle.peakActivation * 100)}%` }} /></i>
                  </label>
                </div>
              </div>
            )}
            {isMobile && isMuscle && (
              <div className="hl-mobile-muscle-actions" data-testid="muscle-mobile-actions">
                <div className="hl-mobile-muscle-note">
                  {pushStressMode
                    ? 'Stress boundary: expected fall'
                    : fatigueMode
                      ? 'Fatigue: reduced torque + delayed control'
                      : weakLeftLeg
                        ? 'Weak left: asymmetric recovery'
                        : 'Certified push: wobble + stay upright'}
                </div>
                <button type="button" data-testid="muscle-mobile-push-left" onClick={() => fireMusclePush('left')}>
                  Push left
                </button>
                <button type="button" data-testid="muscle-mobile-push-forward" onClick={() => fireMusclePush('forward')}>
                  Push fwd
                </button>
                <button type="button" data-testid="muscle-mobile-push-backward" onClick={() => fireMusclePush('backward')}>
                  Push back
                </button>
                <button type="button" data-testid="muscle-mobile-push-right" onClick={() => fireMusclePush('right')}>
                  Push right
                </button>
                <button type="button" className="hl-main-reset" data-testid="muscle-main-reset" onClick={() => setResetToken((value) => value + 1)}>
                  Reset demo
                </button>
              </div>
            )}
          </div>

          {!isMobile && (
            <>
              <div className={`hl-hud ${isMuscle ? 'hl-hud-muscle' : ''}`} data-testid="humanoid-hud">
                {isMuscle ? (
                  <>
                    <StatBlock label="Phase" value={stats.muscle.phase} />
                    <StatBlock label="Activation" value={(stats.muscle.meanActivation * 100).toFixed(0)} suffix="%" />
                    <StatBlock label="Peak act." value={(stats.muscle.peakActivation * 100).toFixed(0)} suffix="%" />
                    <StatBlock label="Fatigue" value={(stats.muscle.fatigue * 100).toFixed(0)} suffix="%" />
                    <StatBlock
                      label="Last push"
                      value={
                        stats.muscle.lastPushDirection
                          ? `${stats.muscle.lastPushDirection} · ${stats.muscle.lastPushTier ?? '—'} · ${stats.muscle.lastPushImpulseNs || '—'} N·s`
                          : '—'
                      }
                    />
                    <StatBlock label="Weak left" value={stats.muscle.weakLeftLeg ? 'ON' : 'OFF'} />
                    <StatBlock label="Pelvis Y" value={stats.pelvisHeight.toFixed(2)} suffix="m" />
                    <StatBlock label="Pelvis tilt" value={stats.muscle.pelvisTiltDeg.toFixed(1)} suffix="°" />
                    <StatBlock label="Support err" value={(stats.muscle.supportErrorM * 100).toFixed(1)} suffix="cm" />
                    <StatBlock label="Forbidden" value={stats.muscle.forbiddenAssistCount.toString()} />
                    <StatBlock label="Step" value={stats.stepMs.toFixed(2)} suffix="ms" />
                    <StatBlock label="Settled" value={stats.settled ? 'YES' : 'NO'} />
                  </>
                ) : (
                  <>
                    <StatBlock label="Bodies" value={stats.bodyCount.toString()} />
                    <StatBlock label="Awake" value={stats.awakeCount.toString()} />
                    <StatBlock label="Step" value={stats.stepMs.toFixed(2)} suffix="ms" />
                    <StatBlock label="Settled" value={stats.settled ? 'YES' : 'NO'} />
                    <StatBlock label="Pelvis Y" value={stats.pelvisHeight.toFixed(2)} suffix="m" />
                    <StatBlock label="Pose-hold" value={stats.poseHoldActive ? 'ON' : 'OFF'} />
                  </>
                )}
              </div>
              {isMuscle && (
                <div className="hl-muscle-limit" data-testid="muscle-live-limitation">
                  Certified push: resist side {MUSCLE_PUSH_CERTIFIED_LATERAL_NS} N·s / front-back{' '}
                  {MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS} N·s. Expected: brace, wobble, stay upright. Stress limit{' '}
                  {MUSCLE_PUSH_STRESS_LATERAL_NS}/{MUSCLE_PUSH_STRESS_SAGITTAL_NS} N·s is expected to fall.
                  Capture step off.
                </div>
              )}
              {isMuscle && <button type="button" className="hl-main-reset" data-testid="muscle-main-reset" onClick={() => setResetToken((value) => value + 1)}>Reset demo</button>}
              {!isMuscleDemoUi && gateStrip}
            </>
          )}
        </section>
      </main>

      {isMobile && (
        <div className="hl-dock">
          {!isFocusedMuscleDemo && <div className="hl-dock-chips">{scenarioChips}</div>}
          <button
            type="button"
            className="hl-dock-toggle"
            data-testid="lab-controls-toggle"
            aria-expanded={controlsOpen}
            onClick={() => setControlsOpen((value) => !value)}
          >
            Controls
          </button>
        </div>
      )}

      {isMobile && (
        <>
          <div
            className={`hl-sheet-backdrop ${controlsOpen ? 'open' : ''}`}
            onClick={() => setControlsOpen(false)}
          />
          <div className={`hl-sheet ${controlsOpen ? 'open' : ''}`}>
            <div className="hl-sheet-handle-row">
              <span className="hl-sheet-handle" />
              <button type="button" className="hl-sheet-close" onClick={() => setControlsOpen(false)} aria-label="Close controls">
                Close
              </button>
            </div>
            <div className="hl-sheet-body">
              {demoGuide}
              {!isMuscleDemoUi && rigSection}
              {!isMuscleDemoUi && activeControlSection}
              {muscleControlSection}
              {resetAction}
              {isMuscle && (
                <div className="hl-muscle-limit" data-testid="muscle-live-limitation">
                  Certified push {MUSCLE_PUSH_CERTIFIED_LATERAL_NS}/{MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS} N·s ·
                  expected wobble + stay upright · stress {MUSCLE_PUSH_STRESS_LATERAL_NS}/
                  {MUSCLE_PUSH_STRESS_SAGITTAL_NS} expected fall
                </div>
              )}
              {!isMuscleDemoUi && gateStrip}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Ground() {
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[40, 40]} />
        <meshStandardMaterial color="#171b22" roughness={0.95} />
      </mesh>
      <gridHelper args={[40, 40, '#3a4250', '#1c2029']} />
    </group>
  );
}

function HumanoidPhysicsLayer({
  scenario,
  limitPreset,
  heightM,
  massKg,
  poseHoldStrength,
  resetToken,
  statsRef,
  onReady,
}: {
  scenario: HumanoidScenario;
  limitPreset: JointLimitPreset;
  heightM: number;
  massKg: number;
  poseHoldStrength: number;
  resetToken: number;
  statsRef: MutableRefObject<HudStats>;
  onReady: () => void;
}) {
  const runtimeRef = useRef<HumanoidLabRuntime | null>(getExistingHumanoidLabRuntime());
  const groupRef = useRef<THREE.Group>(null);
  const visualsRef = useRef<HumanoidVisual[]>([]);
  const staticVisualRef = useRef<StaticSceneryVisual | null>(null);
  const markersRef = useRef<MuscleBalanceMarkers | null>(null);
  const visualsVersionRef = useRef(-1);

  // Bootstrap once. Later scenario/param changes are pushed into the existing
  // runtime by the effects below, matching yardPhysics's getYardRuntime('tower')
  // + setYardStructure(...) split.
  useEffect(() => {
    let cancelled = false;
    getHumanoidLabRuntime(scenario)
      .then((rt) => {
        if (cancelled) return;
        runtimeRef.current = rt;
        onReady();
      })
      .catch((error: unknown) => {
        console.error('Humanoid lab physics failed to initialize', error);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const rt = runtimeRef.current;
    if (rt && rt.scenario !== scenario) setHumanoidLabScenario(rt, scenario);
  }, [scenario]);

  useEffect(() => {
    const rt = runtimeRef.current;
    if (rt && rt.limitPreset !== limitPreset) setHumanoidLabLimitPreset(rt, limitPreset);
  }, [limitPreset]);

  useEffect(() => {
    const rt = runtimeRef.current;
    if (rt && rt.heightM !== heightM) setHumanoidLabHeight(rt, heightM);
  }, [heightM]);

  useEffect(() => {
    const rt = runtimeRef.current;
    if (rt && rt.massKg !== massKg) setHumanoidLabMass(rt, massKg);
  }, [massKg]);

  useEffect(() => {
    const rt = runtimeRef.current;
    if (rt) setHumanoidLabPoseHoldStrength(rt, poseHoldStrength);
  }, [poseHoldStrength]);

  const lastResetToken = useRef(resetToken);
  useEffect(() => {
    const rt = runtimeRef.current;
    if (rt && resetToken !== lastResetToken.current) {
      lastResetToken.current = resetToken;
      respawnHumanoidLabScenario(rt);
    }
  }, [resetToken]);

  const disposeVisuals = useCallback(() => {
    for (const visual of visualsRef.current) {
      groupRef.current?.remove(visual.group);
      visual.dispose();
    }
    visualsRef.current = [];
    if (staticVisualRef.current) {
      groupRef.current?.remove(staticVisualRef.current.group);
      staticVisualRef.current.dispose();
      staticVisualRef.current = null;
    }
    if (markersRef.current) {
      groupRef.current?.remove(markersRef.current.group);
      markersRef.current.dispose();
      markersRef.current = null;
    }
  }, []);

  useEffect(() => () => disposeVisuals(), [disposeVisuals]);

  const { camera, gl } = useThree();

  // Tap/click-to-shove (Alive mode only): a plain 'click' (not drag, so it
  // never fights OrbitControls' own pointerdown/move/up handling) raycasts
  // into the humanoid's tagged meshes (humanoidVisual.ts stamps
  // `mesh.userData.bone`) and applies an impulse to whichever bone was hit,
  // in the camera-forward direction, scaled a bit by how far the hit point
  // is from the pelvis (more leverage at the extremities).
  useEffect(() => {
    const canvas = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const forward = new THREE.Vector3();

    function onClick(event: MouseEvent) {
      const rt = runtimeRef.current;
      const humanoid = rt?.humanoids[0];
      if (!rt || !humanoid || rt.scenario !== 'balance' || !groupRef.current) return;

      const rect = canvas.getBoundingClientRect();
      ndc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const hit = raycaster
        .intersectObjects(groupRef.current.children, true)
        .find((intersection) => typeof intersection.object.userData.bone === 'string');
      if (!hit) return;
      const bone = hit.object.userData.bone as HumanoidBoneName;

      camera.getWorldDirection(forward);
      const hipsOffset = rt.buffer.offsetOf(humanoid.root);
      let distanceScale = 1;
      if (hipsOffset !== undefined) {
        const t = rt.buffer.transforms;
        const distanceFromPelvisM = Math.hypot(
          hit.point.x - t[hipsOffset],
          hit.point.y - t[hipsOffset + 1],
          hit.point.z - t[hipsOffset + 2],
        );
        // ~0m (pelvis itself) -> 0.7x, ~1m+ (an outstretched limb/head) -> 1.5x.
        distanceScale = 0.7 + Math.min(1, distanceFromPelvisM) * 0.8;
      }
      fireShoveImpulse(rt, bone, [forward.x, forward.y, forward.z], distanceScale);
    }

    canvas.addEventListener('click', onClick);
    return () => canvas.removeEventListener('click', onClick);
  }, [camera, gl]);

  // Presentation-only demo time scale: slows wall-clock dt into the fixed-step
  // accumulator during airborne one-leg hold so the lift is readable. Physics
  // still advances identical FIXED_DT steps — only pacing changes.
  const demoTimeScaleRef = useRef(1);
  const demoSlowEaseRef = useRef<{ elapsed: number } | null>(null);
  const wasDemoSlowRef = useRef(false);

  useFrame((_, delta) => {
    const rt = runtimeRef.current;
    if (!rt) return;

    const muscleOnPre = rt.scenario === 'muscleBalance';
    const stepPhasePre = muscleOnPre ? rt.muscleTelemetry.stepPhase : 'none';
    const airbornePre = isAirborneStepPhase(stepPhasePre);
    let demoScale = 1;
    if (airbornePre) {
      demoScale = DEMO_SLOW_MO_SCALE;
      wasDemoSlowRef.current = true;
      demoSlowEaseRef.current = null;
    } else if (wasDemoSlowRef.current && stepPhasePre === 'none') {
      // Short ease back to real-time after the hold ends (~0.3 s wall-clock).
      if (!demoSlowEaseRef.current) demoSlowEaseRef.current = { elapsed: 0 };
      demoSlowEaseRef.current.elapsed += delta;
      const t = Math.min(1, demoSlowEaseRef.current.elapsed / DEMO_SLOW_MO_RAMP_S);
      demoScale = DEMO_SLOW_MO_SCALE + (1 - DEMO_SLOW_MO_SCALE) * t;
      if (t >= 1) {
        wasDemoSlowRef.current = false;
        demoSlowEaseRef.current = null;
        demoScale = 1;
      }
    } else {
      // prepare (or never slow): full speed
      demoScale = 1;
    }
    demoTimeScaleRef.current = demoScale;

    advanceHumanoidLabPhysics(rt, Math.min(delta, 0.1) * demoScale);

    if (visualsVersionRef.current !== rt.scenarioVersion) {
      visualsVersionRef.current = rt.scenarioVersion;
      disposeVisuals();
      visualsRef.current = rt.humanoids.map((humanoid) => {
        const visual = createHumanoidVisual(humanoid, 'mannequin');
        groupRef.current?.add(visual.group);
        return visual;
      });
      // Static scenario geometry (the stairs scenario's 5 boxes) never moves,
      // so it's built once here alongside the humanoid visuals rather than
      // synced per-frame.
      if (rt.stairGeometry.length > 0) {
        const staticVisual = createStaticBoxesVisual(rt.stairGeometry);
        groupRef.current?.add(staticVisual.group);
        staticVisualRef.current = staticVisual;
      }
      if (rt.scenario === 'muscleBalance') {
        const markers = createMuscleBalanceMarkers();
        groupRef.current?.add(markers.group);
        markers.setVisible(true);
        markersRef.current = markers;
      }
    }
    for (const visual of visualsRef.current) visual.sync(rt.buffer);

    const muscleOn = rt.scenario === 'muscleBalance';
    const tel = rt.muscleTelemetry;
    if (muscleOn && visualsRef.current[0]) {
      visualsRef.current[0].setMuscleActivation(tel.groupActivation);
      // Presentation: gold-tint swinging foot during airborne hold phases.
      const swingHighlight =
        isAirborneStepPhase(tel.stepPhase) && (tel.swingSide === 'left' || tel.swingSide === 'right')
          ? tel.swingSide
          : null;
      visualsRef.current[0].setSwingFootHighlight(swingHighlight);
    } else {
      for (const visual of visualsRef.current) {
        visual.setMuscleActivation(null);
        visual.setSwingFootHighlight(null);
      }
    }
    if (markersRef.current) {
      if (muscleOn && tel.controllerActive) {
        markersRef.current.setVisible(true);
        markersRef.current.update(tel.centerOfMass, tel.supportCenter, tel.supportErrorM);
      } else {
        markersRef.current.setVisible(false);
      }
    }

    const stats = statsRef.current;
    stats.bodyCount = rt.bodyCountTotal;
    stats.awakeCount = rt.awakeCount;
    stats.stepMs = rt.stepMs;
    stats.settled = rt.settled;
    stats.pelvisHeight = rt.pelvisHeight;
    stats.poseHoldActive = rt.poseHoldActive;
    stats.gettingUp = rt.gettingUp;
    stats.muscle = muscleOn ? muscleStateFromTelemetry(tel) : { ...EMPTY_MUSCLE };

    window.__humanoidLabState = {
      ready: true,
      scenario: rt.scenario,
      bodyCount: rt.bodyCountTotal,
      awakeCount: rt.awakeCount,
      settled: rt.settled,
      stepMs: rt.stepMs,
      pelvisHeight: rt.pelvisHeight,
      poseHoldActive: rt.poseHoldActive,
      gates: GATES,
      suitePass: SUITE_PASS,
      pelvisDisplacementM: rt.pelvisDisplacementM,
      gettingUp: rt.gettingUp,
      // Additive: only populated while muscleBalance is active so existing
      // scenarios keep a quiet bridge shape (muscle may be undefined or inactive).
      ...(muscleOn ? { muscle: stats.muscle } : {}),
    };
  });

  return <group ref={groupRef} />;
}

function ControlSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="hl-control">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Slider({
  testId,
  label,
  value,
  min,
  max,
  step,
  suffix,
  onChange,
}: {
  testId: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="hl-slider">
      <span>
        {label}
        <strong>
          {value.toFixed(step < 0.1 ? 2 : 0)}
          {suffix}
        </strong>
      </span>
      <input
        data-testid={testId}
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function StatBlock({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="hl-stat">
      <span className="hl-stat-label">{label}</span>
      <span className="hl-stat-value">
        {value}
        {suffix && <em className="hl-stat-suffix">{suffix}</em>}
      </span>
    </div>
  );
}

function MobileStatChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="hl-mobile-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
